import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  ShoppingCart,
  DollarSign,
  AlertCircle,
  Package,
  Calendar,
  Download,
  ArrowRight,
  Eye,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../../lib/currency';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale/tr';
import {
  getSummaryStats,
  getSalesChartData,
  getTopProducts,
  getCategoryStats,
  getRecentOrders,
  getLowStockProducts,
} from '../../services/admin/analytics';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusLabels = {
  pending: 'Ödeme Bekleniyor',
  paid: 'Ödeme Alındı',
  shipped: 'Kargoya Verildi',
  delivered: 'Teslim Edildi',
  cancelled: 'İptal',
};

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summaryStats, setSummaryStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('daily');

  const fetchDashboardData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [stats, sales, products, categories, orders, lowStock] = await Promise.all([
        getSummaryStats(),
        getSalesChartData(chartPeriod, 30),
        getTopProducts(10),
        getCategoryStats(),
        getRecentOrders(5),
        getLowStockProducts(10),
      ]);

      setSummaryStats(stats);
      setSalesData(sales);
      setTopProducts(products);
      setCategoryStats(categories);
      setRecentOrders(orders);
      setLowStockProducts(lowStock);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [chartPeriod]);

  useAdminAutoRefresh(fetchDashboardData, { interval: 8000, deps: [chartPeriod] });

  const handleExportChartData = () => {
    const headers = ['Tarih', 'Satış (₺)', 'Sipariş Sayısı'];
    const rows = salesData.map((item) => [
      item.date,
      item.sales.toFixed(2),
      item.count,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gappaq-satis-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatChartDate = (dateStr) => {
    try {
      if (chartPeriod === 'daily') {
        const date = new Date(dateStr);
        return format(date, 'dd MMM', { locale: tr });
      } else if (chartPeriod === 'weekly') {
        const date = new Date(dateStr);
        return format(date, 'dd MMM', { locale: tr });
      } else {
        const [year, month] = dateStr.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return format(date, 'MMM yyyy', { locale: tr });
      }
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Analitik</h1>
            <p className="text-gray-600 mt-1">Detaylı performans ve satış görünümü</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <SummaryCard
            title="Toplam Satış"
            value={formatCurrency(summaryStats?.totalSales || 0)}
            icon={DollarSign}
            iconColor="text-green-600"
          />
          <SummaryCard
            title="Toplam Sipariş"
            value={summaryStats?.totalOrders || 0}
            icon={ShoppingCart}
            iconColor="text-blue-600"
          />
          <SummaryCard
            title="Ortalama Sepet"
            value={formatCurrency(summaryStats?.averageBasketValue || 0)}
            icon={TrendingUp}
            iconColor="text-purple-600"
          />
          <SummaryCard
            title="Bekleyen Ödeme"
            value={summaryStats?.pendingPayments || 0}
            icon={AlertCircle}
            iconColor="text-yellow-600"
          />
          <SummaryCard
            title="Düşük Stok"
            value={summaryStats?.lowStockProducts || 0}
            icon={Package}
            iconColor="text-red-600"
            onClick={() => navigate('/admin/products')}
            clickable
          />
          <SummaryCard
            title="Bugün Satış"
            value={formatCurrency(summaryStats?.todaySales || 0)}
            icon={Calendar}
            iconColor="text-indigo-600"
          />
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl font-semibold">Satış Grafiği</h2>
            <div className="admin-toolbar admin-toolbar-row flex gap-2">
              <Select value={chartPeriod} onValueChange={setChartPeriod}>
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Günlük</SelectItem>
                  <SelectItem value="weekly">Haftalık</SelectItem>
                  <SelectItem value="monthly">Aylık</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleExportChartData}>
                <Download size={16} className="mr-2" />
                İndir
              </Button>
            </div>
          </div>

          {salesData.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Henüz satış yok, ilk siparişi bekliyoruz!</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatChartDate}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={formatChartDate}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#8884d8"
                  strokeWidth={2}
                  name="Satış (₺)"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-6">En Çok Satan Ürünler</h2>
            {topProducts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Henüz ürün satışı yok</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topProducts.slice(0, 10).map((product, index) => (
                  <div key={product.productId || index} className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-gray-400 font-bold">
                      {index + 1}
                    </div>
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{product.name}</p>
                      <p className="text-sm text-gray-500">
                        {product.quantity} adet • {formatCurrency(product.revenue)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border p-6">
            <h2 className="text-xl font-semibold mb-6">En Popüler Kategoriler</h2>
            {categoryStats.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Henüz kategori verisi yok</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryStats.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {categoryStats.slice(0, 6).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Son Siparişler</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/orders')}>
                Tümünü Gör
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Henüz sipariş yok</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentOrders.map((order) => {
                  let orderDate;
                  if (order.createdAt) {
                    if (order.createdAt.toDate && typeof order.createdAt.toDate === 'function') {
                      orderDate = order.createdAt.toDate();
                    } else if (order.createdAt.seconds) {
                      orderDate = new Date(order.createdAt.seconds * 1000);
                    } else {
                      orderDate = new Date(order.createdAt);
                    }
                  }
                  if (!orderDate || isNaN(orderDate.getTime())) {
                    orderDate = new Date();
                  }
                  return (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/orders/${order.id}`)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm">
                            {order.orderNumber || order.id}
                          </p>
                          <Badge
                            className={statusColors[order.status] || 'bg-gray-100 text-gray-800'}
                          >
                            {statusLabels[order.status] || order.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          {order.customerInfo?.fullName || 'Bilinmiyor'} •{' '}
                          {format(orderDate, 'dd MMM yyyy', { locale: tr })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(order.total || 0)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Düşük Stok Uyarıları</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/products')}>
                Tümünü Gör
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            {lowStockProducts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Düşük stoklu ürün yok</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lowStockProducts.slice(0, 5).map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/products`)}
                  >
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-sm text-red-600 font-medium">
                        Stok: {product.stock} adet
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const SummaryCard = ({ title, value, icon: Icon, iconColor, onClick, clickable }) => {
  return (
    <div
      className={`bg-white rounded-lg border p-6 ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{title}</p>
        <Icon className={iconColor || 'text-gray-400'} size={20} />
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

export default AnalyticsPage;
